import React, { useState } from 'react'
import { Upload, message, Modal } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
const apiUrl = process.env.REACT_APP_API_URL

// 업로드 전 클라이언트에서 줄일 기준. 서버도 2048로 축소하므로 미리 줄여 전송량·실패를 줄인다.
const MAX_DIMENSION = 2048
const JPEG_QUALITY = 0.85

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })

// 선택한 이미지를 캔버스로 다시 그려 리사이즈 + JPEG로 변환한다.
// 대용량 PNG(스크롤 캡쳐)·HEIC 등으로 업로드가 서버에 닿기도 전에 실패하던 문제를
// 표준 JPEG로 통일해 해소한다. (서버 GD는 JPEG/PNG/WebP만 디코딩 가능)
const processImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      let width = img.naturalWidth || img.width
      let height = img.naturalHeight || img.height
      if (!width || !height) {
        reject(new Error('invalid dimension'))
        return
      }
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width >= height) {
          height = Math.round((height * MAX_DIMENSION) / width)
          width = MAX_DIMENSION
        } else {
          width = Math.round((width * MAX_DIMENSION) / height)
          height = MAX_DIMENSION
        }
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      canvas.getContext('2d').drawImage(img, 0, 0, width, height)
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
        'image/jpeg',
        JPEG_QUALITY
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('decode failed'))
    }
    img.src = url
  })

const PhotoUpload = ({ event, setLoading, inputs, setInputs, loading }) => {
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewTitle, setPreviewTitle] = useState('')
  const [fileList, setFileList] = useState([])

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj)
    }
    setPreviewImage(file.url || file.preview)
    setPreviewOpen(true)
    setPreviewTitle(
      file.name || file.url.substring(file.url.lastIndexOf('/') + 1)
    )
  }

  const handleCancel = () => setPreviewOpen(false)

  const uploadProps = {
    name: 'photo',
    multiple: true,
    action:
      event &&
      `${apiUrl}/api/customer/review/${event.storeId}/${event.gifticonCode}/photo`,
    accept: 'image/*',
    listType: 'picture-card',
    fileList: fileList,
    onPreview: handlePreview,
    // 업로드 직전 표준 JPEG로 변환 (antd 4: beforeUpload가 반환한 File/Blob을 그대로 업로드)
    async beforeUpload(file) {
      try {
        const blob = await processImage(file)
        const baseName = (file.name || 'review').replace(/\.[^./\\]+$/, '')
        try {
          return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' })
        } catch (e) {
          // 구형 브라우저: File 생성자 미지원 → Blob에 메타 부여
          blob.name = `${baseName}.jpg`
          return blob
        }
      } catch (e) {
        message.error(
          '사진을 불러오지 못했습니다. 화면 캡쳐(스크린샷) 또는 일반 사진으로 다시 시도해 주세요.'
        )
        return Upload.LIST_IGNORE
      }
    },
    onChange(info) {
      if (info.file.status !== 'uploading') {
        setLoading(true)
      }

      // 파일을 업로드하고나서 서버에서 응답을 받거나 파일을 삭제하면
      if (info.file.status === 'done' || info.file.status === 'removed') {
        const images = info.fileList
          .filter(
            (file) => file.status === 'done' && file.response?.photo?.image
          )
          .map((file) => file.response.photo.image)
        setInputs({
          ...inputs,
          // 남은 이미지의 마지막을 대표로. 마지막 1장을 삭제하면 빈 값이 되어 제출 검증이 올바르게 동작
          image: images[images.length - 1] || '',
          images: images,
        })
        setLoading(false)
      } else if (info.file.status === 'error') {
        // 파일 업로드에 실패하면 (서버가 사유를 주면 함께 표시)
        const serverMsg = info.file.response?.message
        message.error(
          serverMsg ? `사진 등록 실패: ${serverMsg}` : '사진 등록에 실패했습니다..'
        )
        setLoading(false)
        setTimeout(() => {
          // 서버에서 응답을 받지 못하면 fileList에서 삭제
          setFileList(info.fileList.filter((file) => file.status !== 'error'))
        }, 500)
      }
      setFileList(info.fileList)
    },
  }

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div
        style={{
          marginTop: 8,
        }}
      >
        사진 등록
      </div>
    </div>
  )

  return (
    <div>
      <Upload {...uploadProps} showUploadList={true}>
        {fileList.length >= 8 ? null : uploadButton}
      </Upload>
      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
      >
        <img
          alt="example"
          style={{
            width: '100%',
          }}
          src={previewImage}
        />
      </Modal>
    </div>
  )
}

export default PhotoUpload
